
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

const categories = [
  { name: 'Music', icon: 'ri-music-line', gradient: 'from-purple-500 to-pink-500' },
  { name: 'Arts', icon: 'ri-palette-line', gradient: 'from-pink-500 to-rose-500' },
  { name: 'Business', icon: 'ri-briefcase-line', gradient: 'from-blue-500 to-cyan-500' },
  { name: 'Sports', icon: 'ri-football-line', gradient: 'from-green-500 to-emerald-500' },
  { name: 'Food & Drink', icon: 'ri-restaurant-line', gradient: 'from-orange-500 to-amber-500' },
  { name: 'Tech', icon: 'ri-code-s-slash-line', gradient: 'from-indigo-500 to-purple-500' },
  { name: 'Entertainment', icon: 'ri-movie-line', gradient: 'from-red-500 to-pink-500' },
  { name: 'Education', icon: 'ri-book-open-line', gradient: 'from-teal-500 to-cyan-500' },
  { name: 'Wellness', icon: 'ri-heart-pulse-line', gradient: 'from-emerald-500 to-green-500' },
];

export default function Categories() {
  const [attendeeCounts, setAttendeeCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    const fetchCounts = async () => {
      const { data, error } = await supabase
        .from('registrations')
        .select('ticket_quantity, events!inner(category)')
        .eq('payment_status', 'completed');

      if (!error && data) {
        const counts: Record<string, number> = {};
        data.forEach((reg: any) => {
          const category = reg.events?.category;
          if (category) {
            counts[category] = (counts[category] || 0) + (reg.ticket_quantity || 1);
          }
        });
        setAttendeeCounts(counts);
      }
    };
    fetchCounts();
  }, []);

  const formatCount = (count: number) => {
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count.toString();
  };

  return (
    <section id="categories" className="py-16 sm:py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10 sm:mb-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">Browse by Category</h2>
          <p className="text-base sm:text-lg text-gray-600 px-4">From concerts to conferences, find what moves you</p>
        </div>

        <div className="flex items-center justify-center gap-2 sm:gap-3 flex-wrap px-2">
          {categories.map((category) => {
            const count = attendeeCounts[category.name] || 0;
            return (
              <Link
                key={category.name}
                to={`/category/${category.name.toLowerCase().replace(' & ', '-')}`}
                className="group px-4 sm:px-6 py-2.5 sm:py-3 bg-white border border-gray-200 rounded-full hover:border-transparent hover:shadow-lg transition-all duration-300 flex items-center gap-2 sm:gap-3 whitespace-nowrap"
              >
                <div className={`w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-full bg-gradient-to-r ${category.gradient} text-white group-hover:scale-110 transition-transform`}>
                  <i className={`${category.icon} text-xs sm:text-sm`}></i>
                </div>
                <span className="text-xs sm:text-sm font-medium text-gray-700 group-hover:text-gray-900">
                  {category.name}
                </span>
                {count > 0 && (
                  <span className="text-[10px] sm:text-xs font-semibold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">
                    {formatCount(count)} <i className="ri-user-line text-[9px]"></i>
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
